import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Users,
  Search,
  Filter,
  Plus,
  Upload,
  ChevronDown,
  MoreHorizontal,
  Phone,
  Mail,
  RefreshCw,
  ArrowUpDown,
  X,
  Settings2,
  Tag,
  Calendar,
  Clock,
  Trash2,
  Edit,
  Eye,
  Zap,
  Download,
  MoreVertical,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';

// Import shared components
import {
  BulkActionBar,
  AddToAutomationModal,
  AdvancedFiltersPanel,
  SmartListTabs,
} from '../../../components/contacts/ContactsShared';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Agency Contacts Page - GHL-Style CRM Layout
 * 
 * Features:
 * - Full-width table layout (no side column)
 * - Smart Lists as horizontal tabs
 * - Bulk action bar when contacts selected
 * - Add to Automation modal
 * - Advanced Filters right-side panel
 * - Agency-scoped (no cross-agency data)
 */
const AgencyContactsPage = () => {
  const { agencyId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Smart lists
  const [smartLists, setSmartLists] = useState([]);
  const [selectedSmartList, setSelectedSmartList] = useState(null);
  
  // Filter metadata
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, leads: 0, clients: 0 });
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    phone: true,
    email: true,
    status: true,
    contact_type: true,
    created_at: true,
    last_activity_at: true,
    tags: true,
  });
  
  // Selection
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [showAutomationModal, setShowAutomationModal] = useState(false);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!agencyId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedType) params.append('contact_type', selectedType);
      if (selectedSmartList) params.append('smart_list_id', selectedSmartList);
      if (advancedFilters.length > 0) {
        params.append('filters', JSON.stringify(advancedFilters));
      }
      
      const res = await axios.get(`${API}/agencies/${agencyId}/contacts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContacts(res.data.contacts || []);
      setTotalContacts(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [token, agencyId, page, perPage, searchQuery, selectedStatus, selectedType, sortBy, sortOrder, selectedSmartList, advancedFilters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!agencyId) return;
    
    try {
      const res = await axios.get(`${API}/agencies/${agencyId}/contacts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token, agencyId]);

  // Fetch smart lists
  const fetchSmartLists = useCallback(async () => {
    if (!agencyId) return;
    
    try {
      const res = await axios.get(`${API}/agencies/${agencyId}/contacts/smart-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartLists(res.data.smart_lists || []);
    } catch (err) {
      console.error('Failed to fetch smart lists:', err);
    }
  }, [token, agencyId]);

  // Initial load
  useEffect(() => {
    fetchContacts();
    fetchStats();
    fetchSmartLists();
  }, [fetchContacts, fetchStats, fetchSmartLists]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (page === 1) {
        fetchContacts();
      } else {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedContacts(contacts.map(c => c));
    } else {
      setSelectedContacts([]);
    }
  };

  // Handle select all matching (total)
  const handleSelectAllMatching = () => {
    toast.info(`Selected all ${totalContacts} contacts`);
  };

  // Handle individual select
  const handleSelectContact = (contact, checked) => {
    if (checked) {
      setSelectedContacts([...selectedContacts, contact]);
    } else {
      setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
      setSelectAll(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
    });
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'inactive': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'do_not_contact': return 'bg-red-100 text-red-700 border-red-200';
      case 'archived': return 'bg-slate-100 text-slate-500 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Get type badge color
  const getTypeColor = (type) => {
    switch (type) {
      case 'lead': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'prospect': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'client': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'former_client': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'self_repair': return 'bg-teal-100 text-teal-700 border-teal-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Bulk action handlers
  const handleBulkExport = () => {
    toast.info('Exporting contacts...');
  };

  const handleBulkSendEmail = () => {
    toast.info('Opening email composer...');
  };

  const handleBulkAddTags = () => {
    toast.info('Opening tag manager...');
  };

  const handleBulkDelete = () => {
    toast.info(`Delete ${selectedContacts.length} contacts?`);
  };

  const handleBulkMore = (actionId) => {
    if (actionId === 'trigger_automation') {
      setShowAutomationModal(true);
    } else {
      toast.info(`Action: ${actionId}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-contacts-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">Contacts</h1>
              <Badge variant="secondary" className="text-sm">
                {totalContacts.toLocaleString()}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                data-testid="import-contacts-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="add-contact-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Download className="w-4 h-4 mr-2" />
                    Export All
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings2 className="w-4 h-4 mr-2" />
                    Manage Fields
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Smart List Tabs */}
        <SmartListTabs
          smartLists={smartLists}
          selectedList={selectedSmartList}
          onSelectList={setSelectedSmartList}
          onAddList={() => toast.info('Add smart list')}
          totalContacts={stats.total}
        />
      </div>

      {/* Bulk Action Bar */}
      {selectedContacts.length > 0 && (
        <div className="px-6 py-3 bg-white border-b border-slate-200">
          <BulkActionBar
            selectedCount={selectedContacts.length}
            totalCount={totalContacts}
            onSelectAll={handleSelectAllMatching}
            onDeselectAll={() => { setSelectedContacts([]); setSelectAll(false); }}
            onExport={handleBulkExport}
            onTriggerAutomation={() => setShowAutomationModal(true)}
            onSendEmail={handleBulkSendEmail}
            onAddTags={handleBulkAddTags}
            onDelete={handleBulkDelete}
            onMore={handleBulkMore}
            scope="agency"
          />
        </div>
      )}

      {/* Main Content - Full Width */}
      <div className="p-6">
        {/* Toolbar */}
        <div className="flex items-center gap-4 mb-4 bg-white rounded-lg border border-slate-200 p-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-contacts"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Quick Filters */}
          <Select value={selectedStatus || 'all'} onValueChange={(v) => setSelectedStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedType || 'all'} onValueChange={(v) => setSelectedType(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="former_client">Former Client</SelectItem>
              <SelectItem value="self_repair">Self Repair</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters Button */}
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(true)}
            className={cn(
              advancedFilters.length > 0 && 'bg-blue-50 border-blue-200 text-blue-700'
            )}
            data-testid="advanced-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Advanced Filters
            {advancedFilters.length > 0 && (
              <Badge className="ml-2 bg-blue-600 text-white text-xs">
                {advancedFilters.length}
              </Badge>
            )}
          </Button>

          {/* Manage Fields */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="manage-fields-btn">
                <Settings2 className="w-4 h-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {Object.entries(visibleColumns).map(([key, visible]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setVisibleColumns({ ...visibleColumns, [key]: !visible })}
                >
                  <Checkbox checked={visible} className="mr-2" />
                  {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Refresh */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { fetchContacts(); fetchStats(); }}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>

        {/* Contacts Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left w-12">
                    <Checkbox 
                      checked={selectAll && contacts.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  {visibleColumns.name && (
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                      onClick={() => handleSort('last_name')}
                    >
                      <div className="flex items-center gap-1">
                        Contact Name
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  )}
                  {visibleColumns.phone && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Phone
                    </th>
                  )}
                  {visibleColumns.email && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Email
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.contact_type && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Type
                    </th>
                  )}
                  {visibleColumns.created_at && (
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  )}
                  {visibleColumns.last_activity_at && (
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                      onClick={() => handleSort('last_activity_at')}
                    >
                      <div className="flex items-center gap-1">
                        Last Activity
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  )}
                  {visibleColumns.tags && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Tags
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                      <p className="text-slate-500 mt-2">Loading contacts...</p>
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-700 font-medium">No contacts found</p>
                      <p className="text-slate-500 text-sm mt-1">Try adjusting your filters or add a new contact</p>
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <tr 
                      key={contact.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedContacts.some(c => c.id === contact.id)}
                          onCheckedChange={(checked) => handleSelectContact(contact, checked)}
                        />
                      </td>
                      {visibleColumns.name && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-semibold text-sm">
                              {contact.first_name?.[0]}{contact.last_name?.[0]}
                            </div>
                            <div>
                              <button
                                onClick={() => navigate(`/agency/${agencyId}/contacts/${contact.id}`)}
                                className="text-slate-900 font-medium hover:text-teal-600 hover:underline text-left transition-colors"
                                data-testid={`contact-name-${contact.id}`}
                              >
                                {contact.full_name || `${contact.first_name} ${contact.last_name}`}
                              </button>
                              {contact.company_name && (
                                <p className="text-xs text-slate-500">{contact.company_name}</p>
                              )}
                            </div>
                          </div>
                        </td>
                      )}
                      {visibleColumns.phone && (
                        <td className="px-4 py-3">
                          {contact.phone ? (
                            <div className="flex items-center gap-1.5 text-slate-700">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              {contact.phone}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.email && (
                        <td className="px-4 py-3">
                          {contact.email ? (
                            <div className="flex items-center gap-1.5 text-slate-700">
                              <Mail className="w-3.5 h-3.5 text-slate-400" />
                              <span className="truncate max-w-[200px]">{contact.email}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="px-4 py-3">
                          <Badge className={cn('text-xs border', getStatusColor(contact.status))}>
                            {contact.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                      )}
                      {visibleColumns.contact_type && (
                        <td className="px-4 py-3">
                          <Badge className={cn('text-xs border', getTypeColor(contact.contact_type))}>
                            {contact.contact_type?.replace('_', ' ')}
                          </Badge>
                        </td>
                      )}
                      {visibleColumns.created_at && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(contact.created_at)}
                          </div>
                        </td>
                      )}
                      {visibleColumns.last_activity_at && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                            <Clock className="w-3.5 h-3.5" />
                            {formatRelativeTime(contact.last_activity_at)}
                          </div>
                        </td>
                      )}
                      {visibleColumns.tags && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap max-w-[150px]">
                            {contact.tags?.slice(0, 2).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs text-teal-600 border-teal-200 bg-teal-50">
                                {tag}
                              </Badge>
                            ))}
                            {contact.tags?.length > 2 && (
                              <Badge variant="outline" className="text-xs text-slate-500">
                                +{contact.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/agency/${agencyId}/contacts/${contact.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {contacts.length} of {totalContacts} contacts
              </span>
              <Select value={perPage.toString()} onValueChange={(v) => { setPerPage(parseInt(v)); setPage(1); }}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters Side Panel */}
      <AdvancedFiltersPanel
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        onApply={(filters) => {
          setAdvancedFilters(filters);
          setShowAdvancedFilters(false);
          fetchContacts();
        }}
        onClear={() => {
          setAdvancedFilters([]);
          fetchContacts();
        }}
      />

      {/* Add to Automation Modal */}
      <AddToAutomationModal
        isOpen={showAutomationModal}
        onClose={() => setShowAutomationModal(false)}
        selectedContacts={selectedContacts}
        token={token}
        scope="agency"
        agencyId={agencyId}
      />
    </div>
  );
};

export default AgencyContactsPage;
